import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import useFetch from "../../../hooks/useFetch";

const HAFHRoutineList = ({ t, role, hostelCode }) => {

  const authState = useSelector((state) => state.authReducer);
  const [fetchData] = useFetch();

  const [routines, setRoutines] = useState([]);
  const [selectedFilter, setSelectedFilter] = useState("ALL");

  // =====================================================
  // CATEGORY FILTERS
  // =====================================================
  const categories = [
    "WAKEUP",
    "HYGIENE",
    "ROOM",
    "MESS",
    "ATTENDANCE",
    "ACADEMIC",
    "TRANSPORT",
    "STUDY",
    "SPORTS",
    "WELLNESS",
    "DISCIPLINE",
    "SECURITY",
    "VISITOR",
    "CURFEW",
    "CLEANING",
    "MAINTENANCE",
    "SOCIAL",
    "EVENT",
    "OTHER",
  ];

  // =====================================================
  // STATUS FILTERS
  // =====================================================
  const statusFilters = [
    "ACTIVE",
    "INACTIVE",
    "SUSPENDED",
  ];

  // =====================================================
  // FETCH ROUTINES
  // =====================================================
  const fetchRoutines = async () => {
    try {

      const res = await fetchData({
        url: "/hafh/operations/routine/all",
        method: "post",
        data: { hostelCode },
        headers: {
          Authorization: authState.token,
        },
      });

      if (res?.status) {
        setRoutines(res.records || []);
      }

    } catch (err) {
      console.error(err);
    }
  };

  // =====================================================
  // LOAD
  // =====================================================
  useEffect(() => {
    if (hostelCode) {
      fetchRoutines();
    }
  }, [hostelCode]);

  // =====================================================
  // FILTER
  // =====================================================
  const filtered =
    selectedFilter === "ALL"
      ? routines
      : routines.filter(
          (r) =>
            r.routineCategory === selectedFilter ||
            r.routineStatus === selectedFilter
        );

  return (
    <div className="bg-gray-900 p-5 rounded-xl text-white space-y-4">

      {/* ================================================= */}
      {/* HEADER */}
      {/* ================================================= */}
      <h2 className="text-cyan-300 text-lg font-bold">
        📋 Hostel Weekly Routines
      </h2>

      {/* ================================================= */}
      {/* FILTERS */}
      {/* ================================================= */}
      <div className="grid grid-cols-3 lg:grid-cols-7 gap-1">

        <button
          onClick={() => setSelectedFilter("ALL")}
          className={`px-1 py-2 rounded-md text-xs ${
            selectedFilter === "ALL"
              ? "bg-teal-600"
              : "bg-gray-700"
          }`}
        >
          ALL
        </button>

        {[...categories, ...statusFilters].map((f) => (
          <button
            key={f}
            onClick={() => setSelectedFilter(f)}
            className={`px-1 py-2 rounded-md text-xs ${
              selectedFilter === f
                ? "bg-teal-600"
                : "bg-gray-700"
            }`}
          >
            {f}
          </button>
        ))}
      </div>

      {/* ================================================= */}
      {/* LIST */}
      {/* ================================================= */}
      <div className="space-y-3 mt-4">

        {filtered.length === 0 ? (

          <div className="text-gray-400 text-sm">
            No routines found
          </div>

        ) : (

          filtered.map((r) => (

            <div
              key={r._id}
              className="bg-gray-800 p-4 rounded-xl border border-gray-700 space-y-2"
            >

              {/* TITLE */}
              <div className="flex justify-between items-start">

                <div>
                  <div className="text-cyan-300 font-semibold">
                    {r.routineTitle}
                  </div>

                  <div className="text-xs text-gray-400">
                    Code: {r.routineCode}
                  </div>
                </div>

                <div className="text-xs text-yellow-300">
                  {r.routineCategory}
                </div>

              </div>

              {/* DESCRIPTION */}
              <div className="text-sm text-gray-300">
                {r.routineDescription || "No description"}
              </div>

              {/* DAYS */}
              <div className="text-xs text-teal-300">
                📅 {r.applicableDays?.join(", ")}
              </div>

              {/* TIME */}
              <div className="text-xs text-gray-400">
                ⏰ {r.startTime || "N/A"} - {r.endTime || "N/A"}
              </div>

              {/* STATUS */}
              <div className="text-xs">
                Status:{" "}
                <span className="text-green-300">
                  {r.routineStatus}
                </span>
              </div>

              {/* TYPE */}
              <div className="text-xs text-gray-300">
                Type: {r.routineType}
              </div>

              {/* LOCATION */}
              <div className="text-xs text-gray-300">
                📍 {r.venueName || "N/A"} | {r.locationType}
              </div>

              {/* PRIORITY */}
              <div className="text-xs text-orange-300">
                Priority: {r.priorityLevel}
              </div>

              {/* APPLICABLE ROLES */}
              <div className="text-xs text-blue-300">
                Roles:{" "}
                {r.applicableRoles?.join(", ")}
              </div>

              {/* FLAGS */}
              <div className="flex flex-wrap gap-2 text-xs">

                {r.isCompulsory && (
                  <div className="text-red-400">
                    🔴 Compulsory
                  </div>
                )}

                {r.requiresAttendance && (
                  <div className="text-yellow-300">
                    📝 Attendance Required
                  </div>
                )}

                {r.requiresApproval && (
                  <div className="text-purple-300">
                    ✅ Approval Needed
                  </div>
                )}

                {r.isPinned && (
                  <div className="text-cyan-300">
                    📌 Pinned
                  </div>
                )}

              </div>

              {/* RESPONSIBILITY */}
              <div className="text-xs text-gray-400">
                Assigned To:{" "}
                {r.assignedToRole || "N/A"}
                {" | "}
                Supervised By:{" "}
                {r.supervisedByRole || "N/A"}
              </div>

              {/* TAGS */}
              {r.tags?.length > 0 && (

                <div className="flex flex-wrap gap-1 mt-1">

                  {r.tags.map((tag, i) => (
                    <span
                      key={i}
                      className="text-xs bg-gray-700 px-2 py-1 rounded"
                    >
                      #{tag}
                    </span>
                  ))}

                </div>

              )}

              {/* ACTIONS */}
              <div className="flex gap-2 mt-3">

                <button className="px-3 py-1 bg-blue-600 text-xs rounded">
                  View
                </button>

                <button className="px-3 py-1 bg-green-600 text-xs rounded">
                  Edit
                </button>

                <button className="px-3 py-1 bg-purple-600 text-xs rounded">
                  Share
                </button>

              </div>

            </div>

          ))
        )}

      </div>
    </div>
  );
};

export default HAFHRoutineList;